import { useCallback, useEffect, useRef, useState } from 'react';

export type PanelBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PanelMinSize = {
  width: number;
  height: number;
};

type StoredState = {
  bounds: PanelBounds;
  maximized: boolean;
};

function storageKey(panelId: string): string {
  return `floating-panel-${panelId}`;
}

function readStored(panelId?: string): StoredState | null {
  if (!panelId || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(panelId));
    if (!raw) return null;
    return JSON.parse(raw) as StoredState;
  } catch {
    return null;
  }
}

function writeStored(panelId: string | undefined, bounds: PanelBounds, maximized: boolean): void {
  if (!panelId || typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey(panelId), JSON.stringify({ bounds, maximized }));
  } catch {
    /* ignore quota errors */
  }
}

export function clampBounds(bounds: PanelBounds, minSize: PanelMinSize): PanelBounds {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.max(minSize.width, Math.min(bounds.width, vw - 16));
  const height = Math.max(minSize.height, Math.min(bounds.height, vh - 16));
  const x = Math.max(8, Math.min(bounds.x, vw - width - 8));
  const y = Math.max(8, Math.min(bounds.y, vh - height - 8));
  return { x, y, width, height };
}

export function getResponsiveDefaultBounds(defaultBounds: PanelBounds): PanelBounds {
  if (typeof window === 'undefined') return defaultBounds;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (vw < 768) {
    return clampBounds({ x: 8, y: 8, width: vw - 16, height: vh - 16 }, { width: 320, height: 280 });
  }
  return clampBounds(defaultBounds, { width: 320, height: 280 });
}

export function getMaximizedBounds(minSize: PanelMinSize): PanelBounds {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return clampBounds(
    { x: Math.round(vw * 0.01), y: Math.round(vh * 0.03), width: Math.round(vw * 0.98), height: Math.round(vh * 0.94) },
    minSize,
  );
}

type ResizeDir = 'se' | 'e' | 's';

export function useFloatingPanelBounds(
  panelId: string | undefined,
  defaultBounds: PanelBounds,
  minSize: PanelMinSize,
) {
  const stored = readStored(panelId);
  const initial = stored?.bounds
    ? clampBounds(stored.bounds, minSize)
    : getResponsiveDefaultBounds(defaultBounds);

  const [bounds, setBounds] = useState<PanelBounds>(initial);
  const [isMaximized, setIsMaximized] = useState(stored?.maximized ?? false);
  const [isMinimized, setIsMinimized] = useState(false);
  const preMaximizeRef = useRef<PanelBounds | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; orig: PanelBounds } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; orig: PanelBounds; dir: ResizeDir } | null>(null);

  const persist = useCallback(
    (next: PanelBounds, maximized: boolean) => {
      writeStored(panelId, next, maximized);
    },
    [panelId],
  );

  const applyBounds = useCallback(
    (next: PanelBounds, maximized = isMaximized) => {
      const clamped = clampBounds(next, minSize);
      setBounds(clamped);
      persist(clamped, maximized);
    },
    [isMaximized, minSize, persist],
  );

  useEffect(() => {
    const onResize = () => {
      if (isMaximized) {
        applyBounds(getMaximizedBounds(minSize), true);
      } else {
        setBounds((prev) => clampBounds(prev, minSize));
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [applyBounds, isMaximized, minSize]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (dragRef.current) {
        const { startX, startY, orig } = dragRef.current;
        applyBounds({
          ...orig,
          x: orig.x + (e.clientX - startX),
          y: orig.y + (e.clientY - startY),
        });
      }
      if (resizeRef.current) {
        const { startX, startY, orig, dir } = resizeRef.current;
        let width = orig.width;
        let height = orig.height;
        if (dir === 'se' || dir === 'e') width = orig.width + (e.clientX - startX);
        if (dir === 'se' || dir === 's') height = orig.height + (e.clientY - startY);
        applyBounds({ ...orig, width, height });
      }
    };
    const onUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [applyBounds]);

  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      if (isMaximized || isMinimized) return;
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startY: e.clientY, orig: bounds };
    },
    [bounds, isMaximized, isMinimized],
  );

  const startResize = useCallback(
    (e: React.PointerEvent, dir: ResizeDir) => {
      if (isMaximized || isMinimized) return;
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = { startX: e.clientX, startY: e.clientY, orig: bounds, dir };
    },
    [bounds, isMaximized, isMinimized],
  );

  const toggleMaximize = useCallback(() => {
    if (isMinimized) setIsMinimized(false);
    if (isMaximized) {
      const restore = preMaximizeRef.current || getResponsiveDefaultBounds(defaultBounds);
      preMaximizeRef.current = null;
      setIsMaximized(false);
      applyBounds(restore, false);
    } else {
      preMaximizeRef.current = bounds;
      setIsMaximized(true);
      applyBounds(getMaximizedBounds(minSize), true);
    }
  }, [applyBounds, bounds, defaultBounds, isMaximized, isMinimized, minSize]);

  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const restore = useCallback(() => {
    setIsMinimized(false);
  }, []);

  return {
    bounds,
    isMaximized,
    isMinimized,
    startDrag,
    startResize,
    toggleMaximize,
    minimize,
    restore,
  };
}
